import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  useToast,
  Flex,
  IconButton,
  Collapse,
  Tooltip,
  AspectRatio,
  Divider,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronRight, FiMonitor, FiMaximize2, FiPlay, FiPause } from 'react-icons/fi';
import { useVideoStream } from '@/hooks/useVideoStream';

export interface GameButtonEvent {
  buttonId: number;
  action: number;    // 0 = released, 1 = pressed
  timestamp: Date;
}

export interface GameTouchEvent {
  x: number;         // Normalized X coordinate (0-32767)
  y: number;         // Normalized Y coordinate (0-32767)
  action: number;    // 0 = released, 1 = pressed
  timestamp: Date;
}

interface GameInterfaceProps {
  machineId: string;
  sessionId: string;
  isActive: boolean;
  onSendEvent?: (event: GameButtonEvent) => void;
  onSendTouchEvent?: (event: GameTouchEvent) => void;
  streamBaseUrl?: string;
}

// Styled game button in LoginPage style
const GameButton: React.FC<{
  id: number;
  label: string;
  icon: string;
  colorScheme: string;
  isPressed: boolean;
  isDisabled: boolean;
  onPress: () => void;
  onRelease: () => void;
}> = ({ label, icon, colorScheme, isPressed, isDisabled, onPress, onRelease }) => {
  return (
    <Button
      variant="solid"
      colorScheme={colorScheme}
      size="lg"
      h="70px"
      minW="100px"
      flex="1"
      isDisabled={isDisabled}
      onMouseDown={onPress}
      onMouseUp={onRelease}
      onMouseLeave={() => isPressed && onRelease()}
      onTouchStart={(e) => { e.preventDefault(); onPress(); }}
      onTouchEnd={(e) => { e.preventDefault(); onRelease(); }}
      _hover={!isDisabled ? {
        transform: 'translateY(-2px)',
        shadow: 'lg',
        filter: 'brightness(1.1)',
      } : undefined}
      _active={{
        transform: 'scale(0.95)',
        filter: 'brightness(0.9)',
      }}
      transform={isPressed ? 'scale(0.95)' : 'scale(1)'}
      filter={isPressed ? 'brightness(0.9)' : 'brightness(1)'}
      transition="all 0.2s"
      flexDirection="column"
      gap={1}
    >
      <Text fontSize="xl">{icon}</Text>
      <Text fontSize="xs" fontWeight="bold">{label}</Text>
    </Button>
  );
};

export const GameInterface: React.FC<GameInterfaceProps> = ({
  machineId,
  sessionId,
  isActive,
  onSendEvent,
  onSendTouchEvent,
  streamBaseUrl,
}) => {
  const [buttonStates, setButtonStates] = useState<Record<number, boolean>>({});
  const [gameEvents, setGameEvents] = useState<GameButtonEvent[]>([]);
  const [showGeneralView, setShowGeneralView] = useState(true);
  const generalVideoRef = useRef<HTMLVideoElement>(null);
  const toast = useToast();

  const isDark = useColorModeValue(false, true);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Get stream URL for general view
  const getStreamUrl = (path: string) => {
    if (streamBaseUrl) {
      return `${streamBaseUrl}${path}`;
    }
    return `wss://ghost-wheel.ru:50140${path}`;
  };

  // Video stream for general view
  const generalStreamUrl = getStreamUrl('/stream/general');
  const generalStream = useVideoStream({
    wsUrl: generalStreamUrl,
    videoElementId: 'generalVideoStream',
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000,
  });

  // Start general stream when panel is shown and session is active
  useEffect(() => {
    if (showGeneralView && isActive) {
      const timer = setTimeout(() => {
        if (!generalStream.status.connected) {
          generalStream.startStream();
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      generalStream.stopStream();
    }
  }, [showGeneralView, isActive]);

  const handleButtonPress = useCallback((buttonId: number, buttonLabel: string, action: 'press' | 'release') => {
    if (!isActive) return;

    const event: GameButtonEvent = {
      buttonId,
      action: action === 'press' ? 1 : 0,
      timestamp: new Date(),
    };

    setGameEvents(prev => [event, ...prev.slice(0, 9)]);

    if (action === 'press') {
      setButtonStates(prev => ({ ...prev, [buttonId]: true }));
    } else {
      setButtonStates(prev => ({ ...prev, [buttonId]: false }));
    }

    if (onSendEvent) onSendEvent(event);
  }, [machineId, sessionId, isActive, onSendEvent]);

  // Button definitions
  const mainButtons = [
    { id: 0, label: 'СТАРТ', icon: '🎰', colorScheme: 'red' },
    { id: 1, label: 'АВТО', icon: '🔄', colorScheme: 'cyan' },
    { id: 2, label: 'ИГРА', icon: '🎮', colorScheme: 'purple' },
    { id: 4, label: 'ВЫВОД', icon: '💵', colorScheme: 'green' },
    { id: 5, label: 'ЗАБРАТЬ', icon: '💰', colorScheme: 'teal' },
    { id: 6, label: 'МАКС', icon: '💎', colorScheme: 'orange' },
  ];

  const serviceButtons = [
    { id: 3, label: 'СЕРВИС', icon: '⚙️', colorScheme: 'gray' },
    { id: 7, label: 'СТАВКА', icon: '🪙', colorScheme: 'blue' },
    { id: 8, label: 'СЕР.РЕЖ', icon: '🔧', colorScheme: 'gray' },
  ];

  const lineButtons = [
    { id: 10, label: 'ЛИН 1', icon: '1️⃣', colorScheme: 'green' },
    { id: 11, label: 'ЛИН 2', icon: '2️⃣', colorScheme: 'green' },
    { id: 12, label: 'ЛИН 3', icon: '3️⃣', colorScheme: 'green' },
    { id: 13, label: 'ЛИН 4', icon: '4️⃣', colorScheme: 'green' },
    { id: 14, label: 'ЛИН 5', icon: '5️⃣', colorScheme: 'green' },
  ];

  const betButtons = [
    { id: 15, label: 'СТ 1', icon: '🎲', colorScheme: 'yellow' },
    { id: 16, label: 'СТ 2', icon: '🎲', colorScheme: 'yellow' },
    { id: 17, label: 'СТ 3', icon: '🎲', colorScheme: 'yellow' },
    { id: 18, label: 'СТ 4', icon: '🎲', colorScheme: 'yellow' },
    { id: 19, label: 'СТ 5', icon: '🎲', colorScheme: 'yellow' },
  ];

  const allButtons = [...mainButtons, ...serviceButtons, ...lineButtons, ...betButtons];

  return (
    <Flex w="full" gap={4} align="start">
      {/* Main Game Controls Card */}
      <Box flex="1">
        <Card
          w="full"
          bg={cardBg}
          shadow="xl"
          borderRadius="2xl"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={8}>
            <VStack spacing={6}>
              {/* Header */}
              <VStack spacing={2} w="full">
                <Flex w="full" justify="space-between" align="center">
                  <Heading
                    size="lg"
                    bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
                    bgClip="text"
                    fontWeight="bold"
                  >
                    🎮 Управление
                  </Heading>
                  <HStack spacing={2}>
                    <Box
                      w={3}
                      h={3}
                      borderRadius="full"
                      bg={isActive ? "green.400" : "red.400"}
                      boxShadow={isActive ? "0 0 10px #48bb78" : "0 0 10px #fc8181"}
                    />
                    <Text fontSize="sm" color={textColor}>
                      {isActive ? 'Активно' : 'Неактивно'}
                    </Text>
                    <Tooltip label={showGeneralView ? "Скрыть камеру" : "Показать камеру"}>
                      <IconButton
                        aria-label="Toggle general view"
                        icon={<FiMonitor />}
                        size="sm"
                        variant="outline"
                        colorScheme={showGeneralView ? "blue" : "gray"}
                        onClick={() => setShowGeneralView(!showGeneralView)}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
                <Text color={textColor} fontSize="md" w="full">
                  Нажимайте кнопки для управления аппаратом
                </Text>
              </VStack>

              {/* Main Control Buttons */}
              <VStack spacing={4} w="full">
                <Text fontSize="sm" color={textColor} fontWeight="medium">
                  Основные кнопки
                </Text>
                <SimpleGrid columns={3} spacing={3} w="full">
                  {mainButtons.map((button) => (
                    <GameButton
                      key={button.id}
                      id={button.id}
                      label={button.label}
                      icon={button.icon}
                      colorScheme={button.colorScheme}
                      isPressed={buttonStates[button.id] || false}
                      isDisabled={!isActive}
                      onPress={() => handleButtonPress(button.id, button.label, 'press')}
                      onRelease={() => handleButtonPress(button.id, button.label, 'release')}
                    />
                  ))}
                </SimpleGrid>
              </VStack>

              <HStack w="full">
                <Divider borderColor={borderColor} />
                <Text fontSize="sm" color={textColor} px={4} whiteSpace="nowrap">
                  сервис
                </Text>
                <Divider borderColor={borderColor} />
              </HStack>

              {/* Service Buttons */}
              <SimpleGrid columns={3} spacing={3} w="full">
                {serviceButtons.map((button) => (
                  <GameButton
                    key={button.id}
                    id={button.id}
                    label={button.label}
                    icon={button.icon}
                    colorScheme={button.colorScheme}
                    isPressed={buttonStates[button.id] || false}
                    isDisabled={!isActive}
                    onPress={() => handleButtonPress(button.id, button.label, 'press')}
                    onRelease={() => handleButtonPress(button.id, button.label, 'release')}
                  />
                ))}
              </SimpleGrid>

              <HStack w="full">
                <Divider borderColor={borderColor} />
                <Text fontSize="sm" color={textColor} px={4} whiteSpace="nowrap">
                  линии
                </Text>
                <Divider borderColor={borderColor} />
              </HStack>

              {/* Line Selection Buttons */}
              <SimpleGrid columns={5} spacing={2} w="full">
                {lineButtons.map((button) => (
                  <Button
                    key={button.id}
                    variant="solid"
                    colorScheme={button.colorScheme}
                    size="md"
                    h="60px"
                    isDisabled={!isActive}
                    onMouseDown={() => handleButtonPress(button.id, button.label, 'press')}
                    onMouseUp={() => handleButtonPress(button.id, button.label, 'release')}
                    onMouseLeave={() => buttonStates[button.id] && handleButtonPress(button.id, button.label, 'release')}
                    onTouchStart={(e) => { e.preventDefault(); handleButtonPress(button.id, button.label, 'press'); }}
                    onTouchEnd={(e) => { e.preventDefault(); handleButtonPress(button.id, button.label, 'release'); }}
                    _hover={!isActive ? undefined : {
                      transform: 'translateY(-2px)',
                      shadow: 'lg',
                      filter: 'brightness(1.1)',
                    }}
                    _active={{
                      transform: 'scale(0.95)',
                      filter: 'brightness(0.9)',
                    }}
                    transform={buttonStates[button.id] ? 'scale(0.95)' : 'scale(1)'}
                    filter={buttonStates[button.id] ? 'brightness(0.9)' : 'brightness(1)'}
                    transition="all 0.2s"
                    flexDirection="column"
                    gap={0}
                  >
                    <Text fontSize="lg">{button.icon}</Text>
                    <Text fontSize="2xs" fontWeight="bold">{button.label}</Text>
                  </Button>
                ))}
              </SimpleGrid>

              <HStack w="full">
                <Divider borderColor={borderColor} />
                <Text fontSize="sm" color={textColor} px={4} whiteSpace="nowrap">
                  ставки
                </Text>
                <Divider borderColor={borderColor} />
              </HStack>

              {/* Bet Selection Buttons */}
              <SimpleGrid columns={5} spacing={2} w="full">
                {betButtons.map((button) => (
                  <Button
                    key={button.id}
                    variant="solid"
                    colorScheme={button.colorScheme}
                    size="md"
                    h="60px"
                    isDisabled={!isActive}
                    onMouseDown={() => handleButtonPress(button.id, button.label, 'press')}
                    onMouseUp={() => handleButtonPress(button.id, button.label, 'release')}
                    onMouseLeave={() => buttonStates[button.id] && handleButtonPress(button.id, button.label, 'release')}
                    onTouchStart={(e) => { e.preventDefault(); handleButtonPress(button.id, button.label, 'press'); }}
                    onTouchEnd={(e) => { e.preventDefault(); handleButtonPress(button.id, button.label, 'release'); }}
                    _hover={!isActive ? undefined : {
                      transform: 'translateY(-2px)',
                      shadow: 'lg',
                      filter: 'brightness(1.1)',
                    }}
                    _active={{
                      transform: 'scale(0.95)',
                      filter: 'brightness(0.9)',
                    }}
                    transform={buttonStates[button.id] ? 'scale(0.95)' : 'scale(1)'}
                    filter={buttonStates[button.id] ? 'brightness(0.9)' : 'brightness(1)'}
                    transition="all 0.2s"
                    flexDirection="column"
                    gap={0}
                  >
                    <Text fontSize="lg">{button.icon}</Text>
                    <Text fontSize="2xs" fontWeight="bold">{button.label}</Text>
                  </Button>
                ))}
              </SimpleGrid>

              <Divider borderColor={borderColor} />

              {/* Events Log */}
              <VStack spacing={3} w="full">
                <Flex w="full" justify="space-between" align="center">
                  <Text fontSize="sm" color={textColor} fontWeight="medium">
                    📋 История действий
                  </Text>
                  {gameEvents.length > 0 && (
                    <Badge colorScheme="blue" variant="subtle">
                      {gameEvents.length} событий
                    </Badge>
                  )}
                </Flex>
                <Box
                  w="full"
                  maxH="120px"
                  overflowY="auto"
                  bg={sectionBg}
                  borderRadius="xl"
                  p={3}
                >
                  {gameEvents.length === 0 ? (
                    <Text color={textColor} textAlign="center" fontSize="sm">
                      Нажмите кнопки для начала игры
                    </Text>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {gameEvents.map((event) => {
                        const button = allButtons.find(b => b.id === event.buttonId);
                        const actionLabel = event.action === 1 ? 'НАЖАТА' : 'ОТПУЩЕНА';
                        return (
                          <HStack
                            key={`${event.buttonId}-${event.timestamp.getTime()}`}
                            justify="space-between"
                            fontSize="xs"
                          >
                            <HStack>
                              <Badge
                                colorScheme={event.action === 1 ? 'green' : 'red'}
                                size="sm"
                              >
                                {actionLabel}
                              </Badge>
                              <Text fontWeight="medium">
                                {button?.icon} {button?.label || 'Unknown'}
                              </Text>
                            </HStack>
                            <Text color={textColor}>
                              {event.timestamp.toLocaleTimeString()}
                            </Text>
                          </HStack>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Box>

      {/* General View Panel (Collapsible) */}
      <Collapse in={showGeneralView} animateOpacity>
        <Card
          w="320px"
          bg={cardBg}
          shadow="xl"
          borderRadius="2xl"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody p={4}>
            <VStack spacing={4}>
              {/* Header */}
              <Flex w="full" justify="space-between" align="center">
                <HStack spacing={2}>
                  <FiMonitor />
                  <Heading size="sm">Общий вид</Heading>
                </HStack>
                <HStack spacing={1}>
                  <Tooltip label="Полный экран">
                    <IconButton
                      aria-label="Fullscreen"
                      icon={<FiMaximize2 />}
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        const video = generalVideoRef.current;
                        if (video) {
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          } else {
                            video.requestFullscreen();
                          }
                        }
                      }}
                    />
                  </Tooltip>
                  <IconButton
                    aria-label="Close panel"
                    icon={<FiChevronRight />}
                    size="xs"
                    variant="ghost"
                    onClick={() => setShowGeneralView(false)}
                  />
                </HStack>
              </Flex>

              {/* Video Stream */}
              <Box
                w="full"
                bg="black"
                borderRadius="xl"
                overflow="hidden"
                position="relative"
                border="2px"
                borderColor={borderColor}
              >
                <AspectRatio ratio={4/3}>
                  <video
                    ref={generalVideoRef}
                    id="generalVideoStream"
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      background: '#000'
                    }}
                  />
                </AspectRatio>

                {/* Stream indicator */}
                <Box
                  position="absolute"
                  top={2}
                  left={2}
                  bg="blackAlpha.700"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                >
                  <HStack spacing={1}>
                    <Box
                      w={2}
                      h={2}
                      bg={generalStream.status.connected ? "red.500" : "gray.500"}
                      borderRadius="full"
                    />
                    <Text>{generalStream.status.connected ? 'LIVE' : 'OFFLINE'}</Text>
                  </HStack>
                </Box>

                {/* Stream controls */}
                <Box position="absolute" bottom={2} right={2}>
                  <IconButton
                    aria-label={generalStream.status.connected ? "Stop" : "Start"}
                    icon={generalStream.status.connected ? <FiPause /> : <FiPlay />}
                    size="xs"
                    colorScheme={generalStream.status.connected ? "red" : "green"}
                    onClick={() => {
                      if (generalStream.status.connected) {
                        generalStream.stopStream();
                      } else {
                        generalStream.startStream();
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Stream Info */}
              <Box w="full" p={3} bg={sectionBg} borderRadius="xl">
                <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                  Доступные стримы:
                </Text>
                <VStack spacing={1} align="stretch">
                  <HStack justify="space-between">
                    <HStack spacing={1}>
                      <Box w={2} h={2} bg="green.400" borderRadius="full" />
                      <Text fontSize="xs">main</Text>
                    </HStack>
                    <Text fontSize="xs" color={textColor}>/stream/main</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <HStack spacing={1}>
                      <Box w={2} h={2} bg="orange.400" borderRadius="full" />
                      <Text fontSize="xs">general</Text>
                    </HStack>
                    <Text fontSize="xs" color={textColor}>/stream/general</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <HStack spacing={1}>
                      <Box w={2} h={2} bg="blue.400" borderRadius="full" />
                      <Text fontSize="xs">info</Text>
                    </HStack>
                    <Text fontSize="xs" color={textColor}>/stream/info</Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Collapse>
    </Flex>
  );
};
